'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { ArticleTocProps, TocItem } from './articleToc.types';
import styles from './ArticleToc.module.scss';
import {
  EditorJSOutput,
  parseEditorJSData,
} from '@/new/components/ArticleRenderer/editorjs.types';
import { convertToSEOAddress } from '@/lib/utils/convertToSEOAddress';


const ArticleToc: React.FC<ArticleTocProps> = ({
                                                 articleData,
                                                 scrollOffset = 80,
                                                 className = '',
                                               }) => {
  // Мемоизация парсинга данных EditorJS
  const content: EditorJSOutput = useMemo(
    () => parseEditorJSData(articleData),
    [articleData]
  );

  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Извлекаем заголовки уровня H1
  useEffect(() => {
    if (!content?.blocks) return;

    const headers: TocItem[] = content.blocks
      .filter((block) => block.type === 'header' && (block.data.level === 1 || block.data.level === 2))
      .map((block, index) => ({
        id: convertToSEOAddress(block.data.text) || `header-${index}`,
        text: block.data.text,
        level: block.data.level,
      }));

    setTocItems(headers);

    if (headers.length > 0) {
      setActiveId(headers[0].id);
    }
  }, [content]);

  // Прокрутка к заголовку
  const scrollToHeader = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const position = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: position - scrollOffset,
        behavior: 'smooth',
      });
      setActiveId(id);
    }
  };

  // Отслеживание активного заголовка при скролле
  useEffect(() => {
    const handleScroll = () => {
      const headers = tocItems
        .map((item) => document.getElementById(item.id))
        .filter(Boolean) as HTMLElement[];

      if (headers.length === 0) return;

      const closest = headers.reduce(
        (closest, el) => {
          const offset = el.getBoundingClientRect().top - scrollOffset;
          if (offset < 0 && Math.abs(offset) < Math.abs(closest.offset)) {
            return { element: el, offset };
          }
          return closest;
        },
        {
          element: headers[0],
          offset: headers[0].getBoundingClientRect().top - scrollOffset,
        }
      );

      if (closest.element) {
        setActiveId(closest.element.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems, scrollOffset]);

  if (tocItems.length === 0) return null;

  return (
    <nav className={`${styles.tocContainer} ${className}`}>
      <h3 className={styles.tocTitle}>Быстрые переходы</h3>
      <ul className={styles.tocList}>
        {tocItems.map((item) => (
          <li
            key={item.id}
            className={`${styles.tocItem} ${activeId === item.id ? styles.active : ''}`}
            data-level={`h${item.level}`}
          >
            <button
              className={styles.tocLink}
              onClick={() => scrollToHeader(item.id)}
            >
              {item.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default ArticleToc;
